import { TopicGraphNodeData } from './types';

export type TopicGraphDiff = {
  added: number[];
  modified: number[];
  removed: number[];
};

const COMPARE_KEYS: (keyof TopicGraphNodeData)[] = [
  'questionText',
  'type',
  'choices',
  'statements',
  'outputs',
  'reportOrder',
  // 'position' is intentionally excluded: layoutTopicGraph normalizes coordinates during
  // auto-layout, so including it would mark untouched questions as `modified` in the AI diff.
  'range',
  'metrics',
  'isRequired',
  'isQuestionTrigger',
  'input',
  'commentGroupId',
];

const sortRecord = (record: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(record).sort(([a], [b]) => a.localeCompare(b)));

const toComparable = (node: TopicGraphNodeData): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of COMPARE_KEYS) {
    const value = node[key];
    if (value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = sortRecord(value as Record<string, unknown>);
    } else {
      out[key] = value ?? null;
    }
  }
  return out;
};

export const topicGraphNodesSemanticallyEqual = (a: TopicGraphNodeData, b: TopicGraphNodeData): boolean =>
  JSON.stringify(toComparable(a)) === JSON.stringify(toComparable(b));

export const diffTopicGraph = (before: TopicGraphNodeData[], after: TopicGraphNodeData[]): TopicGraphDiff => {
  const beforeMap = new Map(before.map((node) => [node.questionIndex, node]));
  const afterMap = new Map(after.map((node) => [node.questionIndex, node]));

  const added: number[] = [];
  const modified: number[] = [];
  const removed: number[] = [];

  for (const node of after) {
    const previous = beforeMap.get(node.questionIndex);
    if (!previous) {
      added.push(node.questionIndex);
    } else if (!topicGraphNodesSemanticallyEqual(previous, node)) {
      modified.push(node.questionIndex);
    }
  }

  for (const node of before) {
    if (!afterMap.has(node.questionIndex)) {
      removed.push(node.questionIndex);
    }
  }

  return {
    added: added.sort((a, b) => a - b),
    modified: modified.sort((a, b) => a - b),
    removed: removed.sort((a, b) => a - b),
  };
};
